import React, {useCallback} from 'react';

import {PipelineStepDataKind, PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {stepTypeIsConfigEditorSupported} from 'pipelines/generalized-step-config-editor/stepTypeIsConfigEditorSupported';
import useCheckStepTypeEnabledForUser from 'pipelines/PipelineCapabilities';
import {getLatestStepVersion, getOutputStepDatas, getSourceStepTypes} from 'pipelines/StepConnectionUtils';

import StepTypeSelector from '../components/add-step/StepTypeSelector';
import RightSidebarSkeleton, {RightSidebarWidth} from '../components/RightSidebarSkeleton';

/** Step types that cannot be used as replacements for a table source. */
const NON_REPLACEABLE_STEP_TYPES: Partial<Record<PipelineStepType, boolean>> = {
  // Template variables are not a data source — they cannot replace a table.
  [PipelineStepType.DEFINE_TEMPLATE_VARIABLES]: true,
};

/**
 * Filter function for source step types that can replace a table source.
 * Excludes file-producing step types (since the output type must match) and
 * step types that are not meaningful as table replacements.
 */
function filterReplaceableSourceStepTypes(stepType: PipelineStepType): boolean {
  if (
    !stepTypeIsConfigEditorSupported(stepType, getLatestStepVersion(stepType)) ||
    !getSourceStepTypes().includes(stepType)
  ) {
    return false;
  }
  if (NON_REPLACEABLE_STEP_TYPES[stepType]) {
    return false;
  }
  // Only allow step types that produce dataset (table) output, not file output.
  const outputDatas = getOutputStepDatas(stepType);
  return outputDatas.length > 0 && outputDatas.every((o) => o.kind === PipelineStepDataKind.DATASET);
}

/**
 * Step types offered as table replacements in addition to the replaceable source step types. JOIN and
 * UNION_DATASETS are not sources, but let the user build a replacement table from existing tables.
 */
const REPLACE_SOURCE_PSEUDO_STEP_TYPES: ReadonlySet<PipelineStepType> = new Set([
  PipelineStepType.JOIN,
  PipelineStepType.UNION_DATASETS,
]);

interface SelectStepTypePhaseProps {
  selectedStepType: PipelineStepType | null;
  setSelectedStepType: (stepType: PipelineStepType | null) => void;
  onCancel: () => void;
  onNext: () => void;
  /** The pipeline whose source step is being replaced; gates step types by the pipeline's type. */
  pipelineId: string;
}

export default function SelectStepTypePhase({
  selectedStepType,
  setSelectedStepType,
  onCancel,
  onNext,
  pipelineId,
}: SelectStepTypePhaseProps): JSX.Element {
  const checkStepTypeEnabledForUser = useCheckStepTypeEnabledForUser(pipelineId);
  const filterStepTypes = useCallback(
    (stepType: PipelineStepType): boolean =>
      (filterReplaceableSourceStepTypes(stepType) || REPLACE_SOURCE_PSEUDO_STEP_TYPES.has(stepType)) &&
      checkStepTypeEnabledForUser(stepType),
    [checkStepTypeEnabledForUser]
  );

  return (
    <RightSidebarSkeleton
      title="Replace source"
      confirmationButtons={[
        {
          text: 'Cancel',
          onClick: onCancel,
          dataTestAttrsElement: 'cancel',
        },
        {
          text: 'Next',
          onClick: onNext,
          dataTestAttrsElement: 'next',
          disabled: !selectedStepType,
          isPrimary: true,
        },
      ]}
      width={RightSidebarWidth.WIDE}
      dataTestAttrs={{
        component: 'ReplaceSourceStepRightSidebar',
        element: 'root',
      }}
    >
      <StepTypeSelector
        selectedStepType={selectedStepType}
        onSelectStepType={setSelectedStepType}
        filterStepTypes={filterStepTypes}
      />
    </RightSidebarSkeleton>
  );
}
